/**
 * SettingRow - Individual setting display with label, badges, description, and input control
 */
import { Badge } from "@/components/base/badges/badges";
import { SettingInput } from "./setting-input";
import type { Setting } from "@/types/api/settings";

interface SettingRowProps {
    setting: Setting;
    value: unknown;
    onChange: (value: unknown) => void;
    validationError?: string;
}

export function SettingRow({ setting, value, onChange, validationError }: SettingRowProps) {
    const { display_name, description, is_secret, is_editable, requires_restart } = setting;

    return (
        <div className="flex flex-col gap-2 py-3 border-b border-secondary last:border-b-0 sm:flex-row sm:items-start sm:justify-between">
            {/* Left: Label, badges, description */}
            <div className="flex-1 pr-4 mb-2 sm:mb-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-sm font-medium text-primary">{display_name}</label>
                    {is_secret && (
                        <Badge color="gray" size="sm">
                            Secret
                        </Badge>
                    )}
                    {requires_restart && (
                        <Badge color="warning" size="sm">
                            Restart Required
                        </Badge>
                    )}
                    {!is_editable && (
                        <Badge color="gray" size="sm">
                            Read-only
                        </Badge>
                    )}
                </div>
                {description && <p className="text-xs text-tertiary mt-1">{description}</p>}
            </div>

            {/* Right: Input control */}
            <div className="w-full sm:max-w-[min(16rem,100%)] sm:shrink-0 lg:w-52">
                <SettingInput setting={setting} value={value} onChange={onChange} validationError={validationError} />
            </div>
        </div>
    );
}
